// Embed loader: finds [data-caravan-form] containers, fetches each published
// definition, renders live forms, fires the view beacon, and captures
// first-touch attribution. Bundled to dist/embed/forms.v1.js by
// scripts/build-embed.mjs (IIFE); the endpoint constant is baked at build time.
import { renderForm } from './renderForm.js'
import {
  parseUtm, buildFirstTouch, buildContext, viewDedupeKey, safeGet, safeSet, FIRST_TOUCH_KEY,
} from './context.js'

// Replaced by esbuild --define at build time; overridable per container.
// eslint-disable-next-line no-undef
const BUILT_ENDPOINT = typeof __CARAVAN_FORMS_ENDPOINT__ !== 'undefined' ? __CARAVAN_FORMS_ENDPOINT__ : ''

function classMapFrom(container) {
  const map = {}
  for (const slot of ['form', 'row', 'field', 'label', 'input', 'select', 'textarea', 'button', 'error', 'status', 'success']) {
    const v = container.getAttribute(`data-cn-class-${slot}`)
    if (v) map[slot] = v
  }
  return map
}

const FALLBACK_EMAIL = 'hello@careernomad.org'

function showFailureMessage(container) {
  const extra = container.getAttribute('data-cn-class-status')
  const status = document.createElement('p')
  status.className = extra ? `cn-f-status ${extra}` : 'cn-f-status'
  status.setAttribute('role', 'alert')
  const link = document.createElement('a')
  link.href = `mailto:${FALLBACK_EMAIL}`
  link.textContent = FALLBACK_EMAIL
  status.append('Sorry, this form is unavailable right now. Please email us at ', link, '.')
  container.appendChild(status)
}

/**
 * Fetch a form definition, retrying once on a network error or a 5xx. run.app
 * cold-starts on almost every load, and a single blip should not cost a lead.
 * A 4xx (unknown or unpublished slug) is final — retrying cannot fix it.
 * Pure apart from the injected fetch/sleep, so it is unit-testable in node.
 */
export async function loadDefinition(url, { fetchFn = fetch, sleep = (ms) => new Promise((r) => setTimeout(r, ms)), retryDelayMs = 1500 } = {}) {
  let lastErr
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt) await sleep(retryDelayMs)
    try {
      const resp = await fetchFn(url)
      if (resp.ok) return await resp.json()
      lastErr = new Error(String(resp.status))
      if (resp.status < 500) break
    } catch (err) {
      lastErr = err
    }
  }
  throw lastErr
}

function firstTouchRecord() {
  const utm = parseUtm(window.location.search)
  const { record, isNew } = buildFirstTouch(safeGet(window.localStorage, FIRST_TOUCH_KEY), {
    utm,
    referrer: document.referrer || '',
    landingPage: window.location.pathname,
    now: Date.now(),
  })
  if (isNew) safeSet(window.localStorage, FIRST_TOUCH_KEY, JSON.stringify(record))
  return record
}

async function boot(container) {
  // Idempotent: a double-included script tag must not double-render forms.
  if (container.hasAttribute('data-cn-booted')) return
  container.setAttribute('data-cn-booted', '')
  const slug = container.getAttribute('data-caravan-form')
  const endpoint = container.getAttribute('data-caravan-endpoint') || BUILT_ENDPOINT
  if (!slug || !endpoint) return

  // Skeleton while the definition loads (cold starts are real on run.app).
  // Reserve height so the page doesn't jump when the form arrives.
  const skeleton = document.createElement('div')
  skeleton.className = 'cn-f-skeleton'
  skeleton.setAttribute('aria-busy', 'true')
  skeleton.style.minHeight = container.getAttribute('data-caravan-min-height') || '320px'
  container.appendChild(skeleton)

  let def
  try {
    def = await loadDefinition(`${endpoint}/def/${slug}`)
  } catch {
    skeleton.remove()
    console.error('caravan-forms: failed to load definition for', slug)
    showFailureMessage(container)
    return
  }
  skeleton.remove()

  const firstTouch = firstTouchRecord()
  try {
    renderForm(container, def, {
      mode: 'live',
      endpoint,
      classMap: classMapFrom(container),
      submitBody: (base) => ({
        ...base,
        context: buildContext({
          pageUri: window.location.href,
          pageName: document.title,
          referrer: document.referrer || '',
          utm: parseUtm(window.location.search),
          firstTouch,
        }),
      }),
    })

    // Unique-view beacon: once per session per slug+path, only after a
    // successful render (spec §6.4). sendBeacon is text/plain — no preflight.
    const key = viewDedupeKey(slug, window.location.pathname)
    if (safeGet(window.sessionStorage, key) !== '1') {
      if (navigator.sendBeacon && navigator.sendBeacon(`${endpoint}/view/${slug}`, slug)) {
        safeSet(window.sessionStorage, key, '1')
      }
    }
  } catch (err) {
    console.error('caravan-forms: render failed for', slug, err)
    container.textContent = ''
    showFailureMessage(container)
  }
}

export function initCaravanForms(rootDoc) {
  const containers = rootDoc.querySelectorAll('[data-caravan-form]')
  containers.forEach((c) => { boot(c) })
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCaravanForms(document))
  } else {
    initCaravanForms(document)
  }
}
